import { useState, useCallback, useMemo, useEffect } from 'react';
import { useCustomMutation, useCustom, useGo, useApiUrl } from '@refinedev/core';
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  UserPlus,
  Download,
  Package,
  Plus,
  Trash2,
  Send,
  AtSign,
  Phone,
  Hash,
  Mail,
  Tag as TagIcon,
  Check,
  Settings2,
  Globe,
  Bot as BotIcon,
  Wand2,
  X,
  Copy,
  RotateCcw,
  ExternalLink,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormHeader } from '@/components/forms';
import { BotSelect } from '@/components/shared/BotSelect';
import { cn, toYmd } from '@/lib/utils';
import { DatePicker } from '@/components/ui/date-picker';
import { notify } from '@/lib/notify';
import * as XLSX from 'xlsx';
import { useAllPages } from '@/hooks/use-all-pages';
import {
  InvitationItemsBuilder,
  type InvitationItem,
} from './InvitationItemsBuilder';
import type {
  InvitationChannel,
  TelegramSessionOption,
} from './types';
import { parseExcelRows } from './import-parse';

// ============================================================
// Локальные типы строк таблицы импорта (UI-state)
// ============================================================

type EmailLinkTarget = 'PLATFORM' | 'TELEGRAM_BOT';

interface ImportTableRow {
  /** Стабильный id для React key (не сохраняется на бэк). */
  uid: string;
  channel: InvitationChannel;
  /** Email для EMAIL, username/+phone для TELEGRAM. */
  contact: string;
  firstName: string;
  lastName: string;
  items: InvitationItem[];
  tagIds: number[];
  sessionId: number | null;
  botId: number | null;
  emailLinkTarget: EmailLinkTarget;
  /** ISO-дата (YYYY-MM-DD) фиксированного окончания доступа, либо '' если бессрочно/по тарифу. */
  accessEndsAt: string;
  /**
   * Обязателен ли резолв контакта в Telegram (только для TELEGRAM-строк).
   * По умолчанию true. Если false и резолв не удался (скрытый номер/приватный
   * профиль/нет сессии) — строка всё равно создастся, матчинг отложится
   * до регистрации пользователя.
   */
  requireResolved: boolean;
  /**
   * Co-invite: второй член семьи (@username или +phone), если строка выдаёт
   * семейный тариф. Пусто = без co-invite. Сервер сам валидирует, что тариф
   * семейный — если нет, строка вернётся в результатах со статусом failed.
   */
  familyMemberContact: string;
}

interface PerRowResult {
  index: number;
  channel: InvitationChannel;
  contact: string;
  status: 'created' | 'sent' | 'failed';
  invitationId?: number;
  error?: string;
}

/** Категория ошибки bulk-импорта — для группировки и подсказок по действию. */
type FailCategory =
  | 'duplicate'
  | 'not_found'
  | 'session'
  | 'already_registered'
  | 'other';

function classifyFailError(error: string | undefined): FailCategory {
  const e = (error ?? '').toLowerCase();
  if (e.includes('уже существует') || e.includes('уже создано')) {return 'duplicate';}
  if (e.includes('не найден') || e.includes('not found')) {return 'not_found';}
  if (e.includes('сесс') || e.includes('session') || e.includes('telegramuserid')) {
    return 'session';
  }
  if (e.includes('зарегистрирован') || e.includes('в системе')) {return 'already_registered';}
  return 'other';
}

const FAIL_CATEGORY_LABEL: Record<FailCategory, string> = {
  duplicate: 'Уже есть приглашение',
  not_found: 'Не найден в Telegram',
  session: 'Нет Telegram-сессии',
  already_registered: 'Уже в системе',
  other: 'Другая ошибка',
};

const FAIL_CATEGORY_HINT: Record<FailCategory, string> = {
  duplicate: 'Повторно создавать не нужно — открой список и отправь/проверь существующее.',
  not_found:
    'Проверь @username/телефон или верни строки в таблицу с выключенным «Обязательно найти в Telegram».',
  session: 'Авторизуй Telegram-сессию, затем верни ошибки в таблицу и повтори.',
  already_registered: 'Пользователь уже есть — выдавай доступ через профиль, не через приглашение.',
  other: 'Исправь данные в таблице и повтори импорт.',
};

/**
 * Патч bulk-применения к выделенным строкам. Любое заданное (не undefined)
 * поле применяется; tagIds объединяются с существующими, остальные — заменяют.
 * Контакт сюда не входит — он уникален у каждой строки.
 */
interface BulkPatch {
  channel?: InvitationChannel;
  firstName?: string;
  lastName?: string;
  items?: InvitationItem[];
  tagIds?: number[];
  botId?: number | null;
  sessionId?: number | null;
  emailLinkTarget?: EmailLinkTarget;
  accessEndsAt?: string;
  requireResolved?: boolean;
  familyMemberContact?: string;
}

interface TagOption {
  id: number;
  name: string;
  color: string;
  deletedAt: string | null;
}

// ============================================================
// Утилиты
// ============================================================

function emptyRow(channel: InvitationChannel = 'TELEGRAM'): ImportTableRow {
  return {
    uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    channel,
    contact: '',
    firstName: '',
    lastName: '',
    items: [],
    tagIds: [],
    sessionId: null,
    botId: null,
    emailLinkTarget: 'PLATFORM',
    accessEndsAt: '',
    requireResolved: true,
    familyMemberContact: '',
  };
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Контакт валиден если: для EMAIL — email-формат, для TELEGRAM — непустой. */
function rowContactInvalid(row: ImportTableRow): boolean {
  const contact = row.contact.trim();
  if (!contact) {return true;}
  if (row.channel === 'EMAIL') {return !isValidEmail(contact);}
  return false;
}

function rowItemsInvalid(row: ImportTableRow): boolean {
  return row.items.some((i) => i.targetId === null);
}

function downloadTemplate(): void {
  const templateData = [
    {
      'Канал': 'TELEGRAM',
      'Контакт': '@john_doe',
      'user_id': '',
      'Имя': 'John',
      'Фамилия': 'Doe',
      'Тариф': 'Клуб / Yillik (tier id=9)',
      'Co-invite': '',
      'expires': '',
    },
    {
      'Канал': 'TELEGRAM',
      'Контакт': '+998901234567',
      'user_id': '',
      'Имя': 'Jane',
      'Фамилия': 'Smith',
      'Тариф': 'Клуб / Oilaviy yillik (семейный, tier id=19)',
      'Co-invite': '@family_member',
      'expires': '',
    },
    {
      'Канал': 'TELEGRAM',
      'Контакт': '',
      'user_id': '123456789',
      'Имя': 'Bob',
      'Фамилия': 'Brown',
      'Тариф': 'oylik',
      'Co-invite': '',
      'expires': '',
    },
    {
      'Канал': 'EMAIL',
      'Контакт': 'alex@example.com',
      'user_id': '',
      'Имя': 'Alex',
      'Фамилия': '',
      'Тариф': '',
      'Co-invite': '',
      'expires': '',
    },
  ];

  const ws = XLSX.utils.json_to_sheet(templateData);
  ws['!cols'] = [
    { wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
    { wch: 40 }, { wch: 22 }, { wch: 12 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Приглашения');
  XLSX.writeFile(wb, 'шаблон_приглашений.xlsx');
}

/** Excel-строки → ImportTableRow (uid и дефолты UI проставляются здесь). */
function mapParsedToTableRows(
  parsed: ReturnType<typeof parseExcelRows>,
): ImportTableRow[] {
  return parsed.map((p) => {
    const r = emptyRow(p.channel);
    r.contact = p.contact;
    r.firstName = p.firstName;
    r.lastName = p.lastName;
    r.items = p.items.map((it) => ({ ...it }));
    r.accessEndsAt = p.accessEndsAt;
    r.familyMemberContact = p.familyMemberContact;
    return r;
  });
}

// ============================================================
// Главный компонент
// ============================================================

type ImportStep = 'upload' | 'editing' | 'result';

/**
 * Импорт приглашений через редактируемую таблицу.
 *
 * Шаги:
 *  1. upload — загрузка Excel-шаблона ИЛИ старт с пустой таблицы;
 *  2. editing — редактируемая таблица: per-row канал/контакт/доступ/сессия/бот/теги;
 *  3. result — отчёт по каждой строке (created/sent/failed).
 *
 * НЕ блокирует UI при отсутствии Telegram-сессии: строки TELEGRAM без сессии
 * помечаются ошибкой при отправке, EMAIL-строки работают всегда.
 */
/** sessionStorage-ключ отчёта импорта — переживает remount Authenticated после invalidate. */
const IMPORT_REPORT_STORAGE_KEY = 'mc-user-invitations-import-report';

interface StoredImportReport {
  results: PerRowResult[];
  rows: ImportTableRow[];
  savedAt: number;
}

function saveImportReport(results: PerRowResult[], rows: ImportTableRow[]): void {
  try {
    const payload: StoredImportReport = {
      results,
      rows,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(IMPORT_REPORT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode — не блокируем UI
  }
}

function loadImportReport(): StoredImportReport | null {
  try {
    const raw = sessionStorage.getItem(IMPORT_REPORT_STORAGE_KEY);
    if (!raw) {return null;}
    const parsed = JSON.parse(raw) as StoredImportReport;
    if (!parsed || !Array.isArray(parsed.results) || parsed.results.length === 0) {
      return null;
    }
    // Не восстанавливаем отчёт старше 2 часов
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) {
      sessionStorage.removeItem(IMPORT_REPORT_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearImportReport(): void {
  try {
    sessionStorage.removeItem(IMPORT_REPORT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Достаёт { successCount, failedCount, results } из ответа Refine (1–2 обёртки .data). */
function unwrapBulkImportResponse(response: unknown): {
  successCount: number;
  failedCount: number;
  results: PerRowResult[];
} {
  const asRec = (v: unknown): Record<string, unknown> | null =>
    typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;

  let cur: unknown = response;
  for (let i = 0; i < 3; i++) {
    const rec = asRec(cur);
    if (!rec) {break;}
    if ('successCount' in rec || 'failedCount' in rec || Array.isArray(rec.results)) {
      const results = Array.isArray(rec.results) ? (rec.results as PerRowResult[]) : [];
      return {
        successCount: Number(rec.successCount ?? 0),
        failedCount: Number(rec.failedCount ?? 0),
        results,
      };
    }
    cur = rec.data;
  }
  return { successCount: 0, failedCount: 0, results: [] };
}

export const UserInvitationsImport = () => {
  const go = useGo();
  const apiUrl = useApiUrl();
  const [step, setStep] = useState<ImportStep>('upload');
  const [rows, setRows] = useState<ImportTableRow[]>([]);
  const [sendImmediately, setSendImmediately] = useState(true);
  const [submitResults, setSubmitResults] = useState<PerRowResult[]>([]);

  // Восстановить отчёт после remount (глобальный invalidateQueries сбрасывал auth → Outlet).
  useEffect(() => {
    const stored = loadImportReport();
    if (!stored) {return;}
    setSubmitResults(stored.results);
    if (Array.isArray(stored.rows) && stored.rows.length > 0) {
      setRows(stored.rows);
    }
    setStep('result');
  }, []);

  // Сессия / доступные сессии — НЕ блокируем UI, просто показываем подсказку.
  const sessionStatusQuery = useCustom<{ hasAuthorizedSession: boolean }>({
    url: '/user-invitations/session-status',
    method: 'get',
  });
  const hasAnySession =
    sessionStatusQuery.query.data?.data?.hasAuthorizedSession ?? false;

  const sessionsQuery = useCustom<TelegramSessionOption[]>({
    url: `${apiUrl}/telegram-client/accounts`,
    method: 'get',
  });
  const telegramSessions: TelegramSessionOption[] = useMemo(() => {
    const raw = sessionsQuery.query.data?.data;
    if (Array.isArray(raw)) {
      return raw.filter((s) => s.status === 'AUTHORIZED');
    }
    return [];
  }, [sessionsQuery.query.data]);

  // Теги — для multi-select в каждой строке.
  const { data: allTags } = useAllPages<TagOption>({
    resource: 'tags',
    staleTime: 30_000,
  });
  const activeTags = useMemo<TagOption[]>(
    () => allTags.filter((t) => t.deletedAt === null),
    [allTags],
  );
  const tagsById = useMemo(() => {
    const m = new Map<number, TagOption>();
    for (const t of activeTags) {m.set(t.id, t);}
    return m;
  }, [activeTags]);

  const submitMutation = useCustomMutation();
  const isSubmitting = submitMutation.mutation.isPending;

  // ---- Шаг 1: загрузка файла или пустая таблица ----

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {return;}

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        // ArrayBuffer + type:'array' + codepage 65001 (UTF-8) — иначе SheetJS
        // декодирует CSV как latin-1/cp1252 и ломает кириллицу/эмодзи
        // (mojibake: «Дилноз» → «Ð”Ð¸Ð»Ð½Ð¾Ð·», «🎀» → «ðŸŽ€»). Для .xlsx codepage
        // игнорируется (кодировка внутри формата), для .csv — применяется.
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'array', codepage: 65001 });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw:false — даты/числа как строки, иначе expires приходит serial number.
        const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(sheet, {
          defval: '',
          raw: false,
        });

        if (jsonData.length === 0) {
          notify.error('Файл пуст');
          return;
        }

        // Имя листа — фоллбэк тарифа для сплит-файлов (Yillik / Oilaviy / Sardor).
        // Также пробуем имя файла без расширения (1_yillik.xlsx → yillik).
        const fileBase = file.name.replace(/\.[^.]+$/, '');
        const parsed = parseExcelRows(jsonData, {
          sheetName: `${sheetName} ${fileBase}`,
        });
        if (parsed.length === 0) {
          notify.error('Не удалось распознать контакты. Проверь шаблон.');
          return;
        }
        const withAccess = parsed.filter((r) => r.items.length > 0).length;
        const withCoInvite = parsed.filter((r) => r.familyMemberContact.trim().length > 0).length;
        setRows(mapParsedToTableRows(parsed));
        setStep('editing');
        const parts: string[] = [`Загружено ${parsed.length} строк`];
        if (withAccess > 0) {
          parts.push(`доступ у ${withAccess}`);
        } else {
          parts.push('проставьте «Что выдать» массово');
        }
        if (withCoInvite > 0) {
          parts.push(`семейных co-invite: ${withCoInvite}`);
        }
        notify.success(parts.join(' · '));
      } catch {
        notify.error('Ошибка чтения файла');
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const startBlank = useCallback(() => {
    setRows([emptyRow('TELEGRAM')]);
    setStep('editing');
  }, []);

  // ---- Манипуляции со строками ----

  const updateRow = useCallback((uid: string, patch: Partial<ImportTableRow>) => {
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((uid: string) => {
    setRows((prev) => prev.filter((r) => r.uid !== uid));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, emptyRow('TELEGRAM')]);
  }, []);

  // ---- Выделение строк для bulk-действий ----

  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());

  // Чистим выделение от исчезнувших строк (после удаления/сброса).
  const liveSelectedUids = useMemo(() => {
    const present = new Set(rows.map((r) => r.uid));
    const next = new Set<string>();
    for (const uid of selectedUids) {
      if (present.has(uid)) {next.add(uid);}
    }
    return next;
  }, [rows, selectedUids]);

  const toggleRowSelected = useCallback((uid: string, checked: boolean) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (checked) {next.add(uid);} else {next.delete(uid);}
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedUids(checked ? new Set(rows.map((r) => r.uid)) : new Set());
    },
    [rows],
  );

  /**
   * Применяет bulk-патч к выделенным строкам.
   * Поля replace-семантики переписываются целиком; tagIds — объединяются
   * (merge-union) с уже проставленными у строки, чтобы не затирать индивидуальные.
   * Поля доставки применяются с учётом канала каждой строки: emailLinkTarget —
   * только EMAIL-строкам, sessionId — только TELEGRAM-строкам.
   */
  const applyBulk = useCallback(
    (patch: BulkPatch) => {
      const targets = liveSelectedUids;
      if (targets.size === 0) {return;}
      setRows((prev) =>
        prev.map((r) => {
          if (!targets.has(r.uid)) {return r;}
          const next: ImportTableRow = { ...r };
          if (patch.channel !== undefined) {next.channel = patch.channel;}
          if (patch.firstName !== undefined) {next.firstName = patch.firstName;}
          if (patch.lastName !== undefined) {next.lastName = patch.lastName;}
          if (patch.items !== undefined) {
            // Клонируем items, чтобы строки не разделяли один и тот же массив-ссылку.
            next.items = patch.items.map((it) => ({ ...it }));
          }
          if (patch.tagIds !== undefined) {
            const merged = new Set(next.tagIds);
            for (const id of patch.tagIds) {merged.add(id);}
            next.tagIds = [...merged];
          }
          if (patch.botId !== undefined) {next.botId = patch.botId;}
          if (patch.accessEndsAt !== undefined) {next.accessEndsAt = patch.accessEndsAt;}
          // Доставка: применяем к строке только релевантное её каналу поле.
          const effectiveChannel = patch.channel ?? r.channel;
          if (patch.sessionId !== undefined && effectiveChannel === 'TELEGRAM') {
            next.sessionId = patch.sessionId;
          }
          if (patch.requireResolved !== undefined && effectiveChannel === 'TELEGRAM') {
            next.requireResolved = patch.requireResolved;
          }
          if (patch.familyMemberContact !== undefined && effectiveChannel === 'TELEGRAM') {
            next.familyMemberContact = patch.familyMemberContact;
          }
          if (patch.emailLinkTarget !== undefined && effectiveChannel === 'EMAIL') {
            next.emailLinkTarget = patch.emailLinkTarget;
          }
          return next;
        }),
      );
    },
    [liveSelectedUids],
  );

  // ---- Валидация всей таблицы ----

  const validationErrors = useMemo<string[]>(() => {
    const errors: string[] = [];
    if (rows.length === 0) {
      errors.push('Добавьте хотя бы одну строку');
      return errors;
    }
    rows.forEach((row, idx) => {
      const prefix = `Строка #${idx + 1}`;
      if (!row.contact.trim()) {
        errors.push(`${prefix}: укажите контакт`);
      } else if (row.channel === 'EMAIL' && !isValidEmail(row.contact.trim())) {
        errors.push(`${prefix}: некорректный email`);
      }
      if (rowItemsInvalid(row)) {
        errors.push(`${prefix}: заполните выбранные доступы или удалите пустые`);
      }
      // Дубликаты контакта (case-insensitive внутри одного канала)
      const dup = rows.findIndex(
        (r2, j) =>
          j !== idx &&
          r2.channel === row.channel &&
          r2.contact.trim().toLowerCase() === row.contact.trim().toLowerCase() &&
          r2.contact.trim() !== '',
      );
      if (dup !== -1 && dup < idx) {
        errors.push(`${prefix}: дубликат «${row.contact}» (уже встречается в строке #${dup + 1})`);
      }
    });
    return errors;
  }, [rows]);

  const canSubmit = validationErrors.length === 0 && !isSubmitting;

  // ---- Отправка ----

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {return;}

    const payload = {
      sendImmediately,
      rows: rows.map((r) => {
        const contact = r.contact.trim();
        // Контакт из одних цифр (без '+') — это telegram user_id: шлём его явно
        // в telegramUserId, чтобы бэк пропустил резолв и пригласил напрямую по id.
        const isTelegramId = r.channel === 'TELEGRAM' && /^\d{5,}$/.test(contact);
        const familyMemberContact = r.familyMemberContact.trim();
        const familyMemberIsPhone = /^\+?\d{6,}$/.test(familyMemberContact);
        return {
          channel: r.channel,
          contact,
          telegramUserId: isTelegramId ? Number(contact) : undefined,
          telegramFirstName: r.firstName.trim() || null,
          telegramLastName: r.lastName.trim() || null,
          sessionId: r.channel === 'TELEGRAM' ? r.sessionId : null,
          botId: r.botId,
          items: r.items.length > 0 ? r.items : undefined,
          tagIds: r.tagIds.length > 0 ? r.tagIds : undefined,
          emailLinkTarget: r.channel === 'EMAIL' ? r.emailLinkTarget : undefined,
          accessEndsAt: r.accessEndsAt.trim() || undefined,
          requireResolved: r.channel === 'TELEGRAM' ? r.requireResolved : undefined,
          familyMemberTelegramPhone: familyMemberContact && familyMemberIsPhone ? familyMemberContact : undefined,
          familyMemberTelegramUsername:
            familyMemberContact && !familyMemberIsPhone ? familyMemberContact.replace(/^@/, '') : undefined,
        };
      }),
    };

    submitMutation.mutate(
      {
        url: '/user-invitations/import/bulk',
        method: 'post',
        values: payload,
      },
      {
        onSuccess: (response) => {
          // response от useCustomMutation: { data: BulkImportResponse } или уже payload
          const data = unwrapBulkImportResponse(response);
          const results = data.results;
          // snapshot строк для отчёта (имена) + sessionStorage на случай remount
          setSubmitResults(results);
          setStep('result');
          saveImportReport(results, rows);
          if (data.failedCount > 0 || results.some((r) => r.status === 'failed')) {
            notify.warning(
              `Импорт: ${data.successCount} ок, ${data.failedCount} не добавлены — смотри отчёт на экране`,
            );
          } else {
            notify.success(`Импорт: ${data.successCount} приглашений — смотри отчёт на экране`);
          }
        },
        onError: () => {
          notify.error('Ошибка отправки');
        },
      },
    );
  }, [canSubmit, sendImmediately, rows, submitMutation]);

  // ---- Сброс ----

  const handleReset = useCallback(() => {
    clearImportReport();
    setStep('upload');
    setRows([]);
    setSubmitResults([]);
    setSelectedUids(new Set());
  }, []);

  // ============================================================
  // Рендер
  // ============================================================

  return (
    <div className="space-y-6 max-w-[1400px]">
      <FormHeader
        title="Импорт приглашений"
        description="Заполни таблицу или загрузи Excel — на каждой строке свой канал, контакт и доступ"
        sticky
        icon={<Upload className="h-5 w-5" />}
        onBack={() => go({ to: '/user-invitations' })}
      />

      {!hasAnySession && step === 'editing' && (
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800 dark:text-blue-200">
              Telegram-сессия не авторизована
            </p>
            <p className="text-blue-700 dark:text-blue-300 mt-1">
              EMAIL-приглашения работают всегда. Для TELEGRAM-строк нужна сессия —
              авторизуй её или выбери уже расшаренную в колонке «Сессия».
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => go({ to: '/telegram-client' })}
            >
              Перейти к Telegram-аккаунтам
            </Button>
          </div>
        </div>
      )}

      {step === 'upload' && (
        <UploadStep
          onFileUpload={handleFileUpload}
          onStartBlank={startBlank}
        />
      )}

      {step === 'editing' && (
        <EditingStep
          rows={rows}
          telegramSessions={telegramSessions}
          activeTags={activeTags}
          tagsById={tagsById}
          selectedUids={liveSelectedUids}
          validationErrors={validationErrors}
          sendImmediately={sendImmediately}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
          onUpdateRow={updateRow}
          onRemoveRow={removeRow}
          onAddRow={addRow}
          onToggleRowSelected={toggleRowSelected}
          onToggleSelectAll={toggleSelectAll}
          onApplyBulk={applyBulk}
          onSendImmediatelyChange={setSendImmediately}
          onSubmit={handleSubmit}
          onBack={handleReset}
        />
      )}

      {step === 'result' && (
        <ResultStep
          results={submitResults}
          sourceRows={rows}
          onReset={handleReset}
          onGoToList={() => go({ to: '/user-invitations' })}
          onGoToTelegram={() => go({ to: '/telegram-client' })}
          onRetryFailed={(opts) => {
            const failed = submitResults.filter((r) => r.status === 'failed');
            const categories = opts?.onlyCategories;
            const selected = categories
              ? failed.filter((r) => categories.includes(classifyFailError(r.error)))
              : failed;
            const failedIndexes = new Set(selected.map((r) => r.index));
            const next = rows
              .filter((_, i) => failedIndexes.has(i))
              .map((r) => {
                const clone: ImportTableRow = {
                  ...r,
                  uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                  items: r.items.map((it) => ({ ...it })),
                  tagIds: [...r.tagIds],
                };
                if (opts?.disableRequireResolved && clone.channel === 'TELEGRAM') {
                  clone.requireResolved = false;
                }
                return clone;
              });
            if (next.length === 0) {
              notify.warning('Нет строк с ошибками для повтора');
              return;
            }
            setRows(next);
            setSubmitResults([]);
            setSelectedUids(new Set());
            setStep('editing');
            notify.success(
              opts?.disableRequireResolved
                ? `В таблицу вернули ${next.length} (резолв необязателен)`
                : `В таблицу вернули ${next.length} строк с ошибками — исправь и отправь снова`,
            );
          }}
          onRetrySuccessOnly={() => {
            // Только created (не отправленные) — чтобы доотправить
            const pendingIndexes = new Set(
              submitResults.filter((r) => r.status === 'created').map((r) => r.index),
            );
            const next = rows
              .filter((_, i) => pendingIndexes.has(i))
              .map((r) => ({
                ...r,
                uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                items: r.items.map((it) => ({ ...it })),
                tagIds: [...r.tagIds],
              }));
            if (next.length === 0) {
              notify.warning('Нет созданных без отправки');
              return;
            }
            setRows(next);
            setSubmitResults([]);
            setSelectedUids(new Set());
            setSendImmediately(true);
            setStep('editing');
            notify.success(
              `В таблицу ${next.length} созданных без отправки — включена «Отправить сразу»`,
            );
          }}
        />
      )}
    </div>
  );
};

// ============================================================
// Шаг 1: Загрузка / пустая таблица
// ============================================================

interface UploadStepProps {
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStartBlank: () => void;
}

const UploadStep = ({ onFileUpload, onStartBlank }: UploadStepProps) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <FileSpreadsheet className="h-5 w-5" />
        Откуда брать данные
      </CardTitle>
      <CardDescription>
        Загрузите Excel-шаблон или начните с пустой таблицы и добавляйте строки вручную
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/40 transition-colors">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={onFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer block">
            <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">Загрузить Excel/CSV</p>
            <p className="text-sm text-muted-foreground mt-1">
              .xlsx, .xls, .csv — авто-распознавание колонок
            </p>
          </label>
        </div>
        <button
          type="button"
          onClick={onStartBlank}
          className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/40 transition-colors"
        >
          <Plus className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Начать с пустой таблицы</p>
          <p className="text-sm text-muted-foreground mt-1">
            Добавляй строки вручную — каналом, контактом и доступом
          </p>
        </button>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 text-sm">
        <div className="flex items-center justify-between mb-2">
          <p className="font-medium">Шаблон Excel:</p>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Скачать шаблон
          </Button>
        </div>
        <p className="text-muted-foreground">
          Колонки: <span className="font-mono">Канал</span> (TELEGRAM/EMAIL),{' '}
          <span className="font-mono">Контакт</span> (email, @username, +phone или Telegram ID),{' '}
          <span className="font-mono">user_id</span> (опц. — Telegram ID, если нет @username/телефона),{' '}
          <span className="font-mono">Имя</span>, <span className="font-mono">Фамилия</span>,{' '}
          <span className="font-mono">Тариф</span> / <span className="font-mono">plan_type</span>{' '}
          (опц. — свободный текст с <span className="font-mono">tier id=9</span> или alias:{' '}
          <span className="font-mono">yillik</span> / <span className="font-mono">oilaviy</span> /{' '}
          <span className="font-mono">oylik</span> → абонемент «Клуб» подставится автоматически),{' '}
          <span className="font-mono">Co-invite</span> (опц. — @username/+phone второго члена семьи),{' '}
          <span className="font-mono">expires</span> (опц. — дата → «Доступ до»).
          Сплит-файлы по имени листа/файла (Yillik, Oilaviy, Sardor) тоже получают тариф.
          Теги, сессия и бот — в таблице после загрузки.
        </p>
      </div>
    </CardContent>
  </Card>
);

// ============================================================
// Шаг 2: Редактируемая таблица
// ============================================================

interface EditingStepProps {
  rows: ImportTableRow[];
  telegramSessions: TelegramSessionOption[];
  activeTags: TagOption[];
  tagsById: Map<number, TagOption>;
  selectedUids: Set<string>;
  validationErrors: string[];
  sendImmediately: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  onUpdateRow: (uid: string, patch: Partial<ImportTableRow>) => void;
  onRemoveRow: (uid: string) => void;
  onAddRow: () => void;
  onToggleRowSelected: (uid: string, checked: boolean) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onApplyBulk: (patch: BulkPatch) => void;
  onSendImmediatelyChange: (value: boolean) => void;
  onSubmit: () => void;
  onBack: () => void;
}

const EditingStep = ({
  rows,
  telegramSessions,
  activeTags,
  tagsById,
  selectedUids,
  validationErrors,
  sendImmediately,
  isSubmitting,
  canSubmit,
  onUpdateRow,
  onRemoveRow,
  onAddRow,
  onToggleRowSelected,
  onToggleSelectAll,
  onApplyBulk,
  onSendImmediatelyChange,
  onSubmit,
  onBack,
}: EditingStepProps) => {
  const selectedCount = selectedUids.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;
  let selectAllState: boolean | 'indeterminate' = false;
  if (allSelected) {selectAllState = true;}
  else if (someSelected) {selectAllState = 'indeterminate';}

  return (
    <Card>
      <CardHeader>
        <CardTitle>Таблица приглашений</CardTitle>
        <CardDescription>
          В каждой строке выбери канал, контакт, доступы. «Co-invite» — только для семейного тарифа.
          «Сессия», «Бот», «Теги» — опциональные. Отметь строки слева для массового проставления.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <BulkActionsBar
          selectedCount={selectedCount}
          telegramSessions={telegramSessions}
          activeTags={activeTags}
          tagsById={tagsById}
          onApply={onApplyBulk}
          onClearSelection={() => onToggleSelectAll(false)}
        />

        <div className="overflow-x-auto rounded-md border">
        <Table className="min-w-[1720px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={selectAllState}
                  onCheckedChange={(v) => onToggleSelectAll(v === true)}
                  aria-label="Выделить все строки"
                />
              </TableHead>
              <TableHead className="w-10">#</TableHead>
              <TableHead className="w-32">Канал</TableHead>
              <TableHead className="min-w-[220px]">Контакт</TableHead>
              <TableHead className="w-36">Имя</TableHead>
              <TableHead className="w-36">Фамилия</TableHead>
              <TableHead className="min-w-[200px]">Что выдать</TableHead>
              <TableHead className="min-w-[180px]">Co-invite</TableHead>
              <TableHead className="w-40">Доступ до</TableHead>
              <TableHead className="w-48">Сессия / Доставка</TableHead>
              <TableHead className="w-44">Бот</TableHead>
              <TableHead className="w-40">Теги</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => (
              <ImportRow
                key={row.uid}
                row={row}
                index={idx}
                selected={selectedUids.has(row.uid)}
                telegramSessions={telegramSessions}
                activeTags={activeTags}
                tagsById={tagsById}
                onChange={(patch) => onUpdateRow(row.uid, patch)}
                onRemove={() => onRemoveRow(row.uid)}
                onToggleSelected={(checked) => onToggleRowSelected(row.uid, checked)}
              />
            ))}
          </TableBody>
        </Table>
        </div>

        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" size="sm" onClick={onAddRow}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить строку
          </Button>
          <span className="text-sm text-muted-foreground">
            Всего: {rows.length}
            {selectedCount > 0 && ` · выбрано: ${selectedCount}`}
          </span>
        </div>

      {validationErrors.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-1">
          <div className="flex items-center gap-2 text-destructive font-medium text-sm">
            <AlertCircle className="h-4 w-4" />
            Перед отправкой нужно исправить:
          </div>
          <ul className="text-xs text-destructive list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="send-immediately"
            checked={sendImmediately}
            onCheckedChange={(v) => onSendImmediatelyChange(v === true)}
          />
          <Label
            htmlFor="send-immediately"
            className="text-sm font-medium cursor-pointer"
          >
            Отправить сразу после создания
          </Label>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            — иначе создаются в статусе PENDING, отправите вручную из списка
          </span>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : sendImmediately ? (
              <Send className="h-4 w-4 mr-2" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            {sendImmediately ? 'Создать и отправить' : 'Создать'}
            <Badge
              variant="secondary"
              className="ml-2 px-1.5 min-w-5 justify-center bg-primary-foreground/20 text-primary-foreground border-0"
            >
              {rows.length}
            </Badge>
          </Button>
        </div>
      </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Bulk-панель массового изменения выделенных строк
// ============================================================

interface BulkActionsBarProps {
  selectedCount: number;
  telegramSessions: TelegramSessionOption[];
  activeTags: TagOption[];
  tagsById: Map<number, TagOption>;
  onApply: (patch: BulkPatch) => void;
  onClearSelection: () => void;
}

/**
 * Панель массовых действий. Видна только при наличии выделенных строк.
 * Каждый контрол применяет ровно одно поле к выделению — пользователь жмёт
 * «Применить» рядом с нужным полем (а не один общий submit), чтобы можно было
 * проставить, например, только доступы, не трогая остальное.
 *
 * Семантика: все поля заменяют значение в строке целиком; теги — добавляются
 * (merge) к уже проставленным. Доставка (сессия / тип ссылки) применяется к
 * строке только если её канал соответствует.
 */
const BulkActionsBar = ({
  selectedCount,
  telegramSessions,
  activeTags,
  tagsById,
  onApply,
  onClearSelection,
}: BulkActionsBarProps) => {
  // Локальный «черновик» bulk-значений — применяется по кнопке каждого поля.
  const [draftItems, setDraftItems] = useState<InvitationItem[]>([]);
  const [draftTagIds, setDraftTagIds] = useState<number[]>([]);
  const [draftBotId, setDraftBotId] = useState<number | null>(null);
  const [draftChannel, setDraftChannel] = useState<InvitationChannel>('TELEGRAM');
  const [draftSessionId, setDraftSessionId] = useState<number | null>(null);
  const [draftEmailLinkTarget, setDraftEmailLinkTarget] =
    useState<EmailLinkTarget>('PLATFORM');
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [draftAccessEndsAt, setDraftAccessEndsAt] = useState('');
  const [draftRequireResolved, setDraftRequireResolved] = useState(true);

  if (selectedCount === 0) {return null;}

  const draftItemsValid = draftItems.every((i) => i.targetId !== null);

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">
          Массовое изменение — выбрано строк: {selectedCount}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-muted-foreground"
          onClick={onClearSelection}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Снять выделение
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Что выдать (доступы) */}
        <BulkField
          label="Доступы — заменить во всех выбранных"
          onApply={() => onApply({ items: draftItems })}
          applyDisabled={!draftItemsValid}
        >
          <ItemsCell
            items={draftItems}
            invalid={!draftItemsValid}
            onChange={setDraftItems}
          />
        </BulkField>

        {/* Теги (merge) */}
        <BulkField
          label="Теги — добавить к выбранным"
          onApply={() => onApply({ tagIds: draftTagIds })}
          applyDisabled={draftTagIds.length === 0}
        >
          <TagsCell
            selectedIds={draftTagIds}
            allTags={activeTags}
            tagsById={tagsById}
            onChange={setDraftTagIds}
          />
        </BulkField>

        {/* Бот */}
        <BulkField
          label="Бот — заменить"
          onApply={() => onApply({ botId: draftBotId })}
        >
          <BotSelect
            value={draftBotId}
            onChange={setDraftBotId}
            placeholder="По умолчанию"
          />
        </BulkField>

        {/* Канал */}
        <BulkField
          label="Канал — заменить"
          onApply={() => onApply({ channel: draftChannel })}
        >
          <Select
            value={draftChannel}
            onValueChange={(v) => setDraftChannel(v as InvitationChannel)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TELEGRAM">
                <span className="flex items-center gap-2">
                  <Send className="h-3.5 w-3.5" />
                  Telegram
                </span>
              </SelectItem>
              <SelectItem value="EMAIL">
                <span className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </BulkField>

        {/* Сессия (для TELEGRAM-строк) */}
        <BulkField
          label="Сессия — заменить (TELEGRAM-строки)"
          onApply={() => onApply({ sessionId: draftSessionId })}
        >
          <SessionSelect
            value={draftSessionId}
            onChange={setDraftSessionId}
            sessions={telegramSessions}
          />
        </BulkField>

        {/* Тип ссылки (для EMAIL-строк) */}
        <BulkField
          label="Тип ссылки — заменить (EMAIL-строки)"
          onApply={() => onApply({ emailLinkTarget: draftEmailLinkTarget })}
        >
          <Select
            value={draftEmailLinkTarget}
            onValueChange={(v) => setDraftEmailLinkTarget(v as EmailLinkTarget)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PLATFORM">
                <span className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  Ссылка на платформу
                </span>
              </SelectItem>
              <SelectItem value="TELEGRAM_BOT">
                <span className="flex items-center gap-2">
                  <BotIcon className="h-3.5 w-3.5" />
                  Ссылка в Telegram-бот
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </BulkField>

        {/* Имя */}
        <BulkField
          label="Имя — заменить"
          onApply={() => onApply({ firstName: draftFirstName.trim() })}
        >
          <Input
            value={draftFirstName}
            onChange={(e) => setDraftFirstName(e.target.value)}
            placeholder="Имя"
            className="h-9"
          />
        </BulkField>

        {/* Фамилия */}
        <BulkField
          label="Фамилия — заменить"
          onApply={() => onApply({ lastName: draftLastName.trim() })}
        >
          <Input
            value={draftLastName}
            onChange={(e) => setDraftLastName(e.target.value)}
            placeholder="Фамилия"
            className="h-9"
          />
        </BulkField>

        {/* Доступ до (accessEndsAt) — пустая дата = снять ограничение срока */}
        <BulkField
          label="Доступ до — заменить (пусто = бессрочно)"
          onApply={() => onApply({ accessEndsAt: draftAccessEndsAt })}
        >
          <DatePicker
            value={draftAccessEndsAt ? new Date(draftAccessEndsAt) : null}
            onChange={(d) => setDraftAccessEndsAt(d ? toYmd(d) : '')}
            placeholder="Бессрочно"
          />
        </BulkField>

        {/* Обязателен ли резолв в Telegram (TELEGRAM-строки) */}
        <BulkField
          label="Обязательно найти в Telegram — заменить (TELEGRAM-строки)"
          onApply={() => onApply({ requireResolved: draftRequireResolved })}
        >
          <Select
            value={draftRequireResolved ? 'true' : 'false'}
            onValueChange={(v) => setDraftRequireResolved(v === 'true')}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Да, обязателен резолв</SelectItem>
              <SelectItem value="false">Нет, разрешить без резолва</SelectItem>
            </SelectContent>
          </Select>
        </BulkField>
      </div>
    </div>
  );
};

interface BulkFieldProps {
  label: string;
  onApply: () => void;
  applyDisabled?: boolean;
  children: React.ReactNode;
}

/** Одно поле bulk-панели: подпись, контрол и кнопка «Применить» к выделению. */
const BulkField = ({ label, onApply, applyDisabled, children }: BulkFieldProps) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">{children}</div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-9 flex-shrink-0"
        onClick={onApply}
        disabled={applyDisabled}
      >
        <Check className="h-3.5 w-3.5 mr-1" />
        Применить
      </Button>
    </div>
  </div>
);

// ============================================================
// Одна строка таблицы
// ============================================================

interface ImportRowProps {
  row: ImportTableRow;
  index: number;
  selected: boolean;
  telegramSessions: TelegramSessionOption[];
  activeTags: TagOption[];
  tagsById: Map<number, TagOption>;
  onChange: (patch: Partial<ImportTableRow>) => void;
  onRemove: () => void;
  onToggleSelected: (checked: boolean) => void;
}

/**
 * Подсказка под полем контакта Telegram: распознаёт что введено —
 * чистые цифры = telegram user_id (приглашение напрямую по id),
 * +цифры = телефон, иначе = username.
 */
const ContactHint = ({ contact }: { contact: string }) => {
  const value = contact.trim();
  let icon = <AtSign className="h-3 w-3" />;
  let label = 'Поиск по username';
  if (/^\d{5,}$/.test(value)) {
    icon = <Hash className="h-3 w-3" />;
    label = 'Приглашение по Telegram ID';
  } else if (/^\+?\d/.test(value)) {
    icon = <Phone className="h-3 w-3" />;
    label = 'Поиск по телефону';
  }
  return (
    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
      {icon} {label}
    </p>
  );
};

const ImportRow = ({
  row,
  index,
  selected,
  telegramSessions,
  activeTags,
  tagsById,
  onChange,
  onRemove,
  onToggleSelected,
}: ImportRowProps) => {
  const isEmail = row.channel === 'EMAIL';
  const contactInvalid = rowContactInvalid(row);
  const itemsInvalid = rowItemsInvalid(row);

  return (
    <TableRow data-state={selected ? 'selected' : undefined}>
      {/* Выделение */}
      <TableCell className="align-top pt-4">
        <Checkbox
          checked={selected}
          onCheckedChange={(v) => onToggleSelected(v === true)}
          aria-label={`Выделить строку ${index + 1}`}
        />
      </TableCell>

      {/* # */}
      <TableCell className="text-muted-foreground text-xs align-top pt-4">
        {index + 1}
      </TableCell>

      {/* Канал */}
      <TableCell className="align-top">
        <Select
          value={row.channel}
          onValueChange={(v) => onChange({ channel: v as InvitationChannel })}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TELEGRAM">
              <span className="flex items-center gap-2">
                <Send className="h-3.5 w-3.5" />
                Telegram
              </span>
            </SelectItem>
            <SelectItem value="EMAIL">
              <span className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" />
                Email
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>

      {/* Контакт */}
      <TableCell className="align-top">
        <div className="space-y-1">
          <Input
            value={row.contact}
            onChange={(e) => onChange({ contact: e.target.value })}
            placeholder={
              isEmail ? 'user@example.com' : '@username, +998901234567 или 123456789 (ID)'
            }
            className={cn(
              'h-9',
              contactInvalid && row.contact.trim() !== '' && 'border-destructive',
            )}
          />
          {!isEmail && <ContactHint contact={row.contact} />}
          {!isEmail && (
            <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
              <Checkbox
                checked={row.requireResolved}
                onCheckedChange={(v) => onChange({ requireResolved: v === true })}
                className="h-3.5 w-3.5"
              />
              Обязательно найти в Telegram
            </label>
          )}
        </div>
      </TableCell>

      {/* Имя / Фамилия */}
      <TableCell className="align-top">
        <Input
          value={row.firstName}
          onChange={(e) => onChange({ firstName: e.target.value })}
          placeholder="Имя"
          className="h-9"
        />
      </TableCell>
      <TableCell className="align-top">
        <Input
          value={row.lastName}
          onChange={(e) => onChange({ lastName: e.target.value })}
          placeholder="Фамилия"
          className="h-9"
        />
      </TableCell>

      {/* Что выдать */}
      <TableCell className="align-top overflow-hidden">
        <ItemsCell
          items={row.items}
          invalid={itemsInvalid}
          onChange={(items) => onChange({ items })}
        />
      </TableCell>

      {/* Co-invite — отдельная колонка, чтобы не наезжать на «Доступ до» */}
      <TableCell className="align-top overflow-hidden">
        {isEmail ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <div className="space-y-1 min-w-0">
            <Input
              value={row.familyMemberContact}
              onChange={(e) => onChange({ familyMemberContact: e.target.value })}
              placeholder="@user / +998…"
              className="h-9 text-xs min-w-0"
            />
            <p className="text-[10px] text-muted-foreground leading-tight">
              2-й член семьи (семейный тариф)
            </p>
          </div>
        )}
      </TableCell>

      {/* Доступ до (accessEndsAt) — пусто = бессрочно / по тарифу */}
      <TableCell className="align-top overflow-hidden">
        <DatePicker
          value={row.accessEndsAt ? new Date(row.accessEndsAt) : null}
          onChange={(d) => onChange({ accessEndsAt: d ? toYmd(d) : '' })}
          placeholder="—"
          className="h-9 min-w-[9rem] w-full"
        />
      </TableCell>

      {/* Сессия (TELEGRAM) / Тип ссылки (EMAIL) */}
      <TableCell className="align-top">
        {isEmail ? (
          <Select
            value={row.emailLinkTarget}
            onValueChange={(v) => onChange({ emailLinkTarget: v as EmailLinkTarget })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PLATFORM">
                <span className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  Ссылка на платформу
                </span>
              </SelectItem>
              <SelectItem value="TELEGRAM_BOT">
                <span className="flex items-center gap-2">
                  <BotIcon className="h-3.5 w-3.5" />
                  Ссылка в Telegram-бот
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <SessionSelect
            value={row.sessionId}
            onChange={(sessionId) => onChange({ sessionId })}
            sessions={telegramSessions}
          />
        )}
      </TableCell>

      {/* Бот */}
      <TableCell className="align-top">
        <BotSelect
          value={row.botId}
          onChange={(botId) => onChange({ botId })}
          placeholder="По умолчанию"
        />
      </TableCell>

      {/* Теги */}
      <TableCell className="align-top">
        <TagsCell
          selectedIds={row.tagIds}
          allTags={activeTags}
          tagsById={tagsById}
          onChange={(tagIds) => onChange({ tagIds })}
        />
      </TableCell>

      {/* Удалить */}
      <TableCell className="align-top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ============================================================
// Cell-компоненты для popover'ов в таблице
// ============================================================

interface ItemsCellProps {
  items: InvitationItem[];
  invalid: boolean;
  onChange: (items: InvitationItem[]) => void;
}

const ItemsCell = ({ items, invalid, onChange }: ItemsCellProps) => {
  const [open, setOpen] = useState(false);
  const validItems = items.filter((i) => i.targetId !== null);
  const hasItems = items.length > 0;

  const summaryLabel = (() => {
    if (!hasItems) {return 'Без доступа';}
    if (invalid) {return `${validItems.length}/${items.length} — есть пустые`;}
    // Одна строка с тарифом — показываем компактно, чтобы в таблице импорта
    // сразу было видно, что проставилось из Excel (yillik/oilaviy/oylik).
    if (validItems.length === 1) {
      const it = validItems[0];
      if (it.targetType === 'SUBSCRIPTION_PLAN' && it.pricingTierId !== null) {
        return `Абонемент · tier ${it.pricingTierId}`;
      }
      if (it.targetType === 'CHANNEL_PASS') {
        return it.pricingTierId !== null
          ? `Канал · tier ${it.pricingTierId}`
          : 'Канал';
      }
    }
    return `${validItems.length} ${pluralizeAccess(validItems.length)}`;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'h-9 w-full justify-start font-normal min-w-0',
            invalid && 'border-destructive',
            hasItems && !invalid && 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
          )}
        >
          <Package className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
          <span className="truncate">{summaryLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-3" align="start">
        <InvitationItemsBuilder value={items} onChange={onChange} enabled />
      </PopoverContent>
    </Popover>
  );
};

interface SessionSelectProps {
  value: number | null;
  onChange: (value: number | null) => void;
  sessions: TelegramSessionOption[];
}

const SessionSelect = ({ value, onChange, sessions }: SessionSelectProps) => {
  const formatLabel = (s: TelegramSessionOption): string => {
    const name =
      [s.telegramFirstName, s.telegramLastName].filter(Boolean).join(' ') ||
      (s.telegramUsername ? `@${s.telegramUsername}` : s.telegramPhone ?? '') ||
      'Telegram аккаунт';
    if (s.isShared && s.ownerName) {return `${name} (от @${s.ownerName})`;}
    return name;
  };

  const hasSessions = sessions.length > 0;

  return (
    <Select
      value={value !== null ? String(value) : 'auto'}
      onValueChange={(v) => onChange(v === 'auto' ? null : Number(v))}
      disabled={!hasSessions}
    >
      <SelectTrigger className="h-9">
        <SelectValue placeholder={hasSessions ? 'Авто' : 'Нет сессий'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="auto">Авто (активная)</SelectItem>
        {sessions.map((s) => (
          <SelectItem key={s.id} value={String(s.id)}>
            {formatLabel(s)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

interface TagsCellProps {
  selectedIds: number[];
  allTags: TagOption[];
  tagsById: Map<number, TagOption>;
  onChange: (tagIds: number[]) => void;
}

const TagsCell = ({ selectedIds, allTags, tagsById, onChange }: TagsCellProps) => {
  const [open, setOpen] = useState(false);
  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 w-full justify-start font-normal min-w-0"
        >
          <TagIcon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
          <span className="truncate">
            {selectedIds.length === 0
              ? 'Нет'
              : selectedIds.length === 1
                ? tagsById.get(selectedIds[0])?.name ?? '1 тег'
                : `${selectedIds.length} ${pluralizeTags(selectedIds.length)}`}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Поиск тега..." />
          <CommandList>
            <CommandEmpty>Тегов нет</CommandEmpty>
            <CommandGroup>
              {allTags.map((tag) => {
                const checked = selectedIds.includes(tag.id);
                return (
                  <CommandItem
                    key={tag.id}
                    value={`${tag.name}-${tag.id}`}
                    onSelect={() => toggle(tag.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        checked ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span
                      className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ============================================================
// Шаг 3: Результат — кто добавился / кто нет + действия
// ============================================================

type ResultFilter = 'all' | 'ok' | 'failed';

interface ResultStepProps {
  results: PerRowResult[];
  /** Исходные строки таблицы (имя, фамилия, доступы) — для понятного отчёта. */
  sourceRows: ImportTableRow[];
  onReset: () => void;
  onGoToList: () => void;
  onGoToTelegram: () => void;
  /** Вернуть failed-строки в редактор (опционально снять requireResolved). */
  onRetryFailed: (opts?: {
    disableRequireResolved?: boolean;
    onlyCategories?: FailCategory[];
  }) => void;
  /** Вернуть created (без отправки) в редактор с sendImmediately. */
  onRetrySuccessOnly: () => void;
}

interface EnrichedResult extends PerRowResult {
  firstName: string;
  lastName: string;
  displayName: string;
  failCategory: FailCategory | null;
}

const ResultStep = ({
  results,
  sourceRows,
  onReset,
  onGoToList,
  onGoToTelegram,
  onRetryFailed,
  onRetrySuccessOnly,
}: ResultStepProps) => {
  const [filter, setFilter] = useState<ResultFilter>('all');

  const enriched: EnrichedResult[] = useMemo(() => {
    const safeResults = Array.isArray(results) ? results : [];
    const safeSourceRows = Array.isArray(sourceRows) ? sourceRows : [];
    return safeResults.map((r) => {
      const src = safeSourceRows[r.index];
      const firstName = src?.firstName?.trim() ?? '';
      const lastName = src?.lastName?.trim() ?? '';
      const displayName = [firstName, lastName].filter(Boolean).join(' ') || '—';
      return {
        ...r,
        firstName,
        lastName,
        displayName,
        failCategory: r.status === 'failed' ? classifyFailError(r.error) : null,
      };
    });
  }, [results, sourceRows]);

  const sent = useMemo(() => enriched.filter((r) => r.status === 'sent'), [enriched]);
  const created = useMemo(() => enriched.filter((r) => r.status === 'created'), [enriched]);
  const failed = useMemo(() => enriched.filter((r) => r.status === 'failed'), [enriched]);
  const ok = useMemo(() => [...sent, ...created], [sent, created]);

  const failByCategory = useMemo(() => {
    const map = new Map<FailCategory, EnrichedResult[]>();
    for (const r of failed) {
      const cat = r.failCategory ?? 'other';
      const list = map.get(cat) ?? [];
      list.push(r);
      map.set(cat, list);
    }
    return map;
  }, [failed]);

  const visible = useMemo(() => {
    if (filter === 'ok') {return ok;}
    if (filter === 'failed') {return failed;}
    // Ошибки сверху — сразу видно, с кем проблема
    return [...failed, ...ok];
  }, [filter, ok, failed]);

  const copyContacts = useCallback((rows: EnrichedResult[], label: string) => {
    const text = rows.map((r) => r.contact).join('\n');
    if (!text) {
      notify.warning('Нечего копировать');
      return;
    }
    void navigator.clipboard.writeText(text).then(
      () => notify.success(`Скопировано контактов: ${rows.length} (${label})`),
      () => notify.error('Не удалось скопировать'),
    );
  }, []);

  const downloadReportCsv = useCallback(() => {
    const header = ['#', 'Статус', 'Категория', 'Канал', 'Контакт', 'Имя', 'Фамилия', 'ID приглашения', 'Ошибка'];
    const lines = enriched.map((r) => [
      String(r.index + 1),
      r.status,
      r.failCategory ? FAIL_CATEGORY_LABEL[r.failCategory] : '',
      r.channel,
      r.contact,
      r.firstName,
      r.lastName,
      r.invitationId !== null && r.invitationId !== undefined
        ? String(r.invitationId)
        : '',
      r.error ?? '',
    ]);
    const esc = (v: string) => `"${v.replaceAll('"', '""')}"`;
    const csv = [header, ...lines].map((row) => row.map(esc).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-invitations-result-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notify.success('CSV отчёта скачан');
  }, [enriched]);

  const hasNotFound = (failByCategory.get('not_found')?.length ?? 0) > 0;
  const hasSessionFail = (failByCategory.get('session')?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Результат импорта
          </CardTitle>
          <CardDescription>
            Ниже — кто добавился, кто нет, почему и что можно сделать дальше.
            Ошибки показаны первыми.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={cn(
                'rounded-lg border p-3 text-center transition-colors',
                filter === 'all' ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
              )}
            >
              <p className="text-2xl font-bold">{enriched.length}</p>
              <p className="text-xs text-muted-foreground">Всего строк</p>
            </button>
            <button
              type="button"
              onClick={() => setFilter('ok')}
              className={cn(
                'rounded-lg border p-3 text-center transition-colors',
                filter === 'ok'
                  ? 'border-green-500 bg-green-50 dark:bg-green-950'
                  : 'hover:bg-muted/40',
              )}
            >
              <p className="text-2xl font-bold text-green-600">{ok.length}</p>
              <p className="text-xs text-green-700 dark:text-green-300">
                Успешно ({sent.length} отпр. / {created.length} создано)
              </p>
            </button>
            <button
              type="button"
              onClick={() => setFilter('failed')}
              className={cn(
                'rounded-lg border p-3 text-center transition-colors',
                filter === 'failed'
                  ? 'border-red-500 bg-red-50 dark:bg-red-950'
                  : 'hover:bg-muted/40',
              )}
            >
              <p className="text-2xl font-bold text-red-600">{failed.length}</p>
              <p className="text-xs text-red-700 dark:text-red-300">Не добавлены</p>
            </button>
            <div className="rounded-lg border p-3 text-center bg-muted/30">
              <p className="text-2xl font-bold text-blue-600">{created.length}</p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Создано, но не отправлено
              </p>
            </div>
          </div>

          {/* Сводка по типам ошибок */}
          {failed.length > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
              <div className="flex items-center gap-2 font-medium text-sm">
                <AlertCircle className="h-4 w-4 text-destructive" />
                Почему не добавились ({failed.length})
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {[...failByCategory.entries()].map(([cat, list]) => (
                  <div
                    key={cat}
                    className="rounded-md border bg-background/80 p-3 text-sm space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{FAIL_CATEGORY_LABEL[cat]}</span>
                      <Badge variant="secondary">{list.length}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{FAIL_CATEGORY_HINT[cat]}</p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">
                      {list.slice(0, 3).map((x) => x.contact).join(', ')}
                      {list.length > 3 ? ` +${list.length - 3}` : ''}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" size="sm" onClick={() => onRetryFailed()}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Вернуть ошибки в таблицу ({failed.length})
                </Button>
                {hasNotFound && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      onRetryFailed({
                        disableRequireResolved: true,
                        onlyCategories: ['not_found'],
                      })
                    }
                  >
                    <Users className="h-3.5 w-3.5 mr-1.5" />
                    «Не найден» → в таблицу без резолва
                    ({failByCategory.get('not_found')?.length ?? 0})
                  </Button>
                )}
                {hasSessionFail && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onGoToTelegram}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Авторизовать Telegram-сессию
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => copyContacts(failed, 'ошибки')}
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                  Копировать контакты ошибок
                </Button>
              </div>
            </div>
          )}

          {created.length > 0 && (
            <div className="rounded-lg border border-blue-300/50 bg-blue-50/50 dark:bg-blue-950/30 p-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-blue-800 dark:text-blue-200">
                {created.length} приглашений созданы, но не отправлены в Telegram
                (галка «Отправить сразу» была выкл. или отправка упала).
              </span>
              <Button type="button" size="sm" variant="secondary" onClick={onRetrySuccessOnly}>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Вернуть для отправки
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onGoToList}>
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Открыть список
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">
              {filter === 'failed'
                ? `Не добавлены (${failed.length})`
                : filter === 'ok'
                  ? `Успешно (${ok.length})`
                  : `Все строки (${enriched.length})`}
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={downloadReportCsv}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Скачать CSV
              </Button>
              {ok.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => copyContacts(ok, 'успешные')}
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                  Контакты успешных
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[28rem] rounded-md border">
            <Table className="min-w-[880px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead className="w-28">Статус</TableHead>
                  <TableHead className="w-40">Тип</TableHead>
                  <TableHead className="min-w-[140px]">Контакт</TableHead>
                  <TableHead className="min-w-[140px]">Имя</TableHead>
                  <TableHead className="w-24">ID</TableHead>
                  <TableHead>Причина / комментарий</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Нет строк в этом фильтре
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((r) => (
                    <TableRow
                      key={`${r.index}-${r.contact}`}
                      className={cn(
                        r.status === 'failed' && 'bg-red-50/40 dark:bg-red-950/20',
                        r.status === 'created' && 'bg-blue-50/30 dark:bg-blue-950/10',
                        r.status === 'sent' && 'bg-green-50/30 dark:bg-green-950/10',
                      )}
                    >
                      <TableCell className="text-muted-foreground text-xs">
                        {r.index + 1}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            'gap-1',
                            r.status === 'sent' && 'text-green-700 border-green-300',
                            r.status === 'created' && 'text-blue-700 border-blue-300',
                            r.status === 'failed' && 'text-red-700 border-red-300',
                          )}
                        >
                          {r.status === 'sent' && <CheckCircle className="h-3 w-3" />}
                          {r.status === 'created' && <Check className="h-3 w-3" />}
                          {r.status === 'failed' && <XCircle className="h-3 w-3" />}
                          {r.status === 'sent'
                            ? 'Отправлено'
                            : r.status === 'created'
                              ? 'Создано'
                              : 'Не добавлен'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.failCategory ? (
                          <span className="text-muted-foreground">
                            {FAIL_CATEGORY_LABEL[r.failCategory]}
                          </span>
                        ) : r.status === 'sent' ? (
                          <span className="text-green-700 dark:text-green-400">Доставлено</span>
                        ) : (
                          <span className="text-blue-700 dark:text-blue-400">В системе, ждёт отправки</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.contact}</TableCell>
                      <TableCell className="text-sm">{r.displayName}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.invitationId !== null && r.invitationId !== undefined
                          ? `#${r.invitationId}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[320px]">
                        {r.error ?? (r.status === 'created' ? 'Создано без отправки' : '—')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap gap-3 justify-between pt-4">
            <Button variant="outline" onClick={onReset}>
              <Upload className="h-4 w-4 mr-2" />
              Новый импорт
            </Button>
            <div className="flex flex-wrap gap-2">
              {failed.length > 0 && (
                <Button variant="secondary" onClick={() => onRetryFailed()}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Исправить ошибки
                </Button>
              )}
              <Button onClick={onGoToList}>
                <ExternalLink className="h-4 w-4 mr-2" />
                К списку приглашений
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Pluralization helpers
// ============================================================

function pluralizeAccess(n: number): string {
  if (n === 1) {return 'доступ';}
  if (n >= 2 && n <= 4) {return 'доступа';}
  return 'доступов';
}

function pluralizeTags(n: number): string {
  if (n === 1) {return 'тег';}
  if (n >= 2 && n <= 4) {return 'тега';}
  return 'тегов';
}
